/**
 * ChangeList Component (AC-572)
 *
 * Displays a list of changes (create, update, delete projects) for
 * project entities with filtering, pagination, and actions.
 *
 * Parent Component: HistoryPage (right panel) and EntityHistoryTab
 *
 * Features:
 * - Display list of changes with entity type icon, entity name, project badge, changed fields, timestamp
 * - Project badges: create (green), update (yellow/amber), delete (red)
 * - Filter dropdown by entity type (All, Model, Agent, Workflow)
 * - "View Details" button opens ChangeDetailModal
 * - "Re-apply" button calls store.reapplyChange()
 * - Pagination with "Load More" button
 * - Loading and error states
 * - Empty state when no changes
 */

import React, { useState, useMemo } from 'react';
import { Bot, Database, GitBranch, Eye, RotateCcw, Undo2, Loader2, ListOrdered, ChevronDown, Check } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';
import { ChangeSummary, ChangeDetail, ChangeFilters, CheckpointSummary } from '../../types/version_control';
import { apiService } from '../../services/api';
import ChangeDetailModal from './ChangeDetailModal';
import { analyzeStepChanges, StepChangesSummary, getStepDisplayName } from './stepChangeUtils';
import Tooltip from '../common/Tooltip';

interface ChangeListProps {
  changes: ChangeSummary[];
  projectId: number;
  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
  entityTypeFilter?: string;
  onFilterChange?: (filters: ChangeFilters) => void;
  /** Compact mode for narrow panels - stacks layout vertically */
  compact?: boolean;
  /** ISO timestamp - changes newer than this will be highlighted with a "New" badge */
  highlightChangesSince?: string | null;
  /** Callback when entity is changed via undo/reapply - parent should refresh entity data */
  onEntityChanged?: () => void;
  /** Optional checkpoints array - enables "Since last checkpoint" filter when provided */
  checkpoints?: CheckpointSummary[];
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  } else if (diffDay === 1) {
    return 'yesterday';
  } else if (diffDay < 30) {
    return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  } else {
    const diffMonth = Math.floor(diffDay / 30);
    return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  }
}

/**
 * Get the icon component for an entity type
 */
function EntityTypeIcon({
  entityType,
}: {
  entityType: 'agent' | 'model' | 'orchestration' | 'queue';
}) {
  switch (entityType) {
    case 'agent':
      return <Bot className="w-4 h-4" data-testid="icon-agent" />;
    case 'model':
      return <Database className="w-4 h-4" data-testid="icon-model" />;
    case 'orchestration':
      return <GitBranch className="w-4 h-4" data-testid="icon-workflow" />;
    case 'queue':
      return <ListOrdered className="w-4 h-4" data-testid="icon-queue" />;
    default:
      return null;
  }
}

/**
 * Get entity type icon wrapper styling
 */
function getEntityIconClasses(entityType: 'agent' | 'model' | 'orchestration' | 'queue'): string {
  switch (entityType) {
    case 'agent':
      return 'bg-blue-100 dark:bg-blue-900/30 text-primary-600 dark:text-primary-400';
    case 'model':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    case 'orchestration':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
    case 'queue':
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  }
}

/**
 * Get project badge styling
 */
function getOperationBadgeClasses(operation: 'create' | 'update' | 'delete' | 'restore' | 'reapply' | 'undo' | 'revert'): string {
  switch (operation) {
    case 'create':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 green success';
    case 'update':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 yellow warning amber';
    case 'delete':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 red danger destructive';
    case 'restore':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    case 'reapply':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
    case 'undo':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    case 'revert':
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  }
}

/**
 * Check if a change is the latest for its entity.
 * Changes are sorted newest first, so we check if this is the first
 * occurrence of the entity_type + entity_id combination.
 */
function isLatestChangeForEntity(
  change: ChangeSummary | ChangeDetail,
  allChanges: ChangeSummary[]
): boolean {
  // Find the first change (newest) for this entity
  const latestForEntity = allChanges.find(
    (c) => c.entity_type === change.entity_type && c.entity_id === change.entity_id
  );
  return latestForEntity?.change_id === change.change_id;
}

/**
 * Check if a change should be highlighted as "new" based on threshold timestamp
 */
function isNewChange(change: ChangeSummary, highlightSince: string | null): boolean {
  if (!highlightSince) return false;
  const changeDate = new Date(change.created_at);
  const thresholdDate = new Date(highlightSince);
  return changeDate > thresholdDate;
}

/**
 * StepChangeBadges - Displays colored badges for step changes
 */
interface StepChangeBadgesProps {
  stepChanges: StepChangesSummary;
}

const StepChangeBadges: React.FC<StepChangeBadgesProps> = ({ stepChanges }) => {
  const { added, deleted, modified } = stepChanges;

  const formatStepNames = (steps: { label?: string; step_type: string }[]) =>
    steps.map(s => getStepDisplayName(s)).join(', ');

  return (
    <div className="flex flex-wrap gap-1" data-testid="step-change-badges">
      {added.length > 0 && (
        <span
          className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium"
          aria-label={`${added.length} step${added.length !== 1 ? 's' : ''} added: ${formatStepNames(added)}`}
        >
          +{added.length} added: {formatStepNames(added)}
        </span>
      )}
      {deleted.length > 0 && (
        <span
          className="text-xs px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium"
          aria-label={`${deleted.length} step${deleted.length !== 1 ? 's' : ''} deleted: ${formatStepNames(deleted)}`}
        >
          -{deleted.length} deleted: {formatStepNames(deleted)}
        </span>
      )}
      {modified.length > 0 && (
        <span
          className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 font-medium"
          aria-label={`${modified.length} step${modified.length !== 1 ? 's' : ''} modified: ${formatStepNames(modified.map(m => m.after))}`}
        >
          ~{modified.length} modified: {formatStepNames(modified.map(m => m.after))}
        </span>
      )}
    </div>
  );
};

const ChangeList: React.FC<ChangeListProps> = ({
  changes,
  projectId,
  loading = false,
  error = null,
  hasMore = false,
  onLoadMore,
  entityTypeFilter = '',
  onFilterChange,
  compact = false,
  highlightChangesSince = null,
  onEntityChanged,
  checkpoints = [],
}) => {
  const [selectedChange, setSelectedChange] = useState<ChangeDetail | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reapplyingId, setReapplyingId] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [filter, setFilter] = useState(entityTypeFilter || 'all');
  const [timeFilter, setTimeFilter] = useState<'all' | 'since_checkpoint'>('all');
  const [undoingId, setUndoingId] = useState<number | null>(null);
  const [showUndoConfirm, setShowUndoConfirm] = useState<number | null>(null);
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  const { loadChanges, reapplyChange, undoChange } = useVersionStore();

  // Get the latest checkpoint timestamp for "Since last checkpoint" filter
  const latestCheckpoint = useMemo(() => {
    if (checkpoints.length === 0) return null;
    // Checkpoints are sorted newest first
    return checkpoints[0];
  }, [checkpoints]);

  // Compute step changes for workflow updates (for inline badges)
  const stepChangesMap = useMemo(() => {
    const map = new Map<number, StepChangesSummary | null>();
    changes.forEach(change => {
      if (change.entity_type === 'orchestration' &&
          change.changed_fields?.includes('steps')) {
        // Cast to ChangeDetail to access previous_state and new_state
        const detail = change as unknown as ChangeDetail;
        if (detail.previous_state !== undefined || detail.new_state !== undefined) {
          map.set(change.change_id, analyzeStepChanges(
            detail.previous_state as Record<string, unknown> | null,
            detail.new_state as Record<string, unknown> | null
          ));
        }
      }
    });
    return map;
  }, [changes]);

  // Build filters object based on current filter state
  const buildFilters = (entityType: string, timeFilterValue: 'all' | 'since_checkpoint'): ChangeFilters => {
    const filters: ChangeFilters = {};
    if (entityType !== 'all') {
      filters.entity_type = entityType as 'agent' | 'model' | 'orchestration';
    }
    if (timeFilterValue === 'since_checkpoint' && latestCheckpoint) {
      filters.since = latestCheckpoint.created_at;
    }
    return filters;
  };

  // Handle entity type filter change
  const handleFilterChange = (value: string) => {
    setFilter(value);
    setShowEntityDropdown(false);

    const filters = buildFilters(value, timeFilter);
    onFilterChange?.(filters);
    loadChanges(projectId, filters);
  };

  // Handle time filter change
  const handleTimeFilterChange = (value: 'all' | 'since_checkpoint') => {
    setTimeFilter(value);
    setShowTimeDropdown(false);

    const filters = buildFilters(filter, value);
    onFilterChange?.(filters);
    loadChanges(projectId, filters);
  };

  // Handle view details - fetch full change detail from API
  const handleViewDetails = async (change: ChangeSummary) => {
    setLoadingDetailId(change.change_id);
    try {
      const changeDetail = await apiService.getChange(projectId, change.change_id);
      setSelectedChange(changeDetail);
      setModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch change details:', err);
      // Fallback to summary data if fetch fails
      const changeDetail: ChangeDetail = {
        ...change,
        previous_state: null,
        new_state: null,
        checkpoint_id: null,
        metadata: null,
      };
      setSelectedChange(changeDetail);
      setModalOpen(true);
    } finally {
      setLoadingDetailId(null);
    }
  };

  // Handle re-apply
  const handleReapply = async (changeId: number) => {
    setReapplyingId(changeId);
    try {
      await reapplyChange(projectId, changeId);
      // Notify parent to refresh entity data
      onEntityChanged?.();
    } catch (err) {
      // Error handling is done in the store
    } finally {
      setReapplyingId(null);
    }
  };

  // Handle undo click - show confirmation dialog
  const handleUndoClick = (changeId: number) => {
    setShowUndoConfirm(changeId);
  };

  // Handle undo confirm - call undoChange
  const handleUndoConfirm = async () => {
    const changeId = showUndoConfirm;
    if (changeId === null) return;

    setShowUndoConfirm(null);
    setUndoingId(changeId);
    try {
      await undoChange(projectId, changeId);
      // Refresh the list after undo
      const filters = buildFilters(filter, timeFilter);
      await loadChanges(projectId, filters);
      // Notify parent to refresh entity data
      onEntityChanged?.();
    } catch (err) {
      // Error handling is done in the store
    } finally {
      setUndoingId(null);
    }
  };

  // Handle undo cancel - close confirmation dialog
  const handleUndoCancel = () => {
    setShowUndoConfirm(null);
  };

  // Handle load more
  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const filters = buildFilters(filter, timeFilter);
      filters.offset = changes.length;
      await loadChanges(projectId, filters);
      onLoadMore?.();
    } catch (err) {
      // Error handling is done in the store
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle retry
  const handleRetry = () => {
    const filters = buildFilters(filter, timeFilter);
    loadChanges(projectId, filters);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedChange(null);
  };

  // Handle reapplied callback (from modal)
  const handleReapplied = () => {
    // Refresh the list after reapply
    const filters = buildFilters(filter, timeFilter);
    loadChanges(projectId, filters);
  };

  // Handle undone callback (from modal)
  const handleUndone = () => {
    // Refresh the list after undo
    const filters = buildFilters(filter, timeFilter);
    loadChanges(projectId, filters);
  };

  // Loading state
  if (loading && changes.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading changes...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button
          onClick={handleRetry}
          className="px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
          aria-label="Retry"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty state
  if (changes.length === 0) {
    return (
      <div className="space-y-4">
        {/* Filter dropdowns */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Entity:</span>
            <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowEntityDropdown(false); }}>
              <button
                type="button"
                onClick={() => setShowEntityDropdown(!showEntityDropdown)}
                className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white hover:bg-gray-50 dark:hover:bg-gray-700 min-w-[120px]"
                aria-label="Filter by entity type"
              >
                <span>{{ all: 'All', model: 'Model', agent: 'Agent', orchestration: 'Workflow', queue: 'Queue' }[filter] || 'All'}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showEntityDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showEntityDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'model', label: 'Model' },
                    { value: 'agent', label: 'Agent' },
                    { value: 'orchestration', label: 'Workflow' },
                    { value: 'queue', label: 'Queue' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleFilterChange(option.value)}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                    >
                      <span className={filter === option.value ? 'font-medium text-primary-600' : 'text-gray-700 dark:text-gray-300'}>{option.label}</span>
                      {filter === option.value && <Check className="w-4 h-4 text-primary-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Time filter - only show if checkpoints exist */}
          {latestCheckpoint && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Time:</span>
              <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowTimeDropdown(false); }}>
                <button
                  type="button"
                  onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white hover:bg-gray-50 dark:hover:bg-gray-700 min-w-[180px]"
                  aria-label="Filter by time"
                >
                  <span>{timeFilter === 'all' ? 'All time' : 'Since last checkpoint'}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTimeDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showTimeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                    {[
                      { value: 'all' as const, label: 'All time' },
                      { value: 'since_checkpoint' as const, label: 'Since last checkpoint' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleTimeFilterChange(option.value)}
                        className="flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                      >
                        <span className={timeFilter === option.value ? 'font-medium text-primary-600' : 'text-gray-700 dark:text-gray-300'}>{option.label}</span>
                        {timeFilter === option.value && <Check className="w-4 h-4 text-primary-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            {timeFilter === 'since_checkpoint' ? 'No changes since last checkpoint' : 'No changes yet'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {timeFilter === 'since_checkpoint'
              ? 'All changes are checkpointed'
              : 'Changes will appear here when you create, update, or delete entities'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-2" : "space-y-4"}>
      {/* Filter dropdowns - hidden in compact mode */}
      {!compact && (
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Entity:</span>
            <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowEntityDropdown(false); }}>
              <button
                type="button"
                onClick={() => setShowEntityDropdown(!showEntityDropdown)}
                className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white hover:bg-gray-50 dark:hover:bg-gray-700 min-w-[120px]"
                aria-label="Filter by entity type"
              >
                <span>{{ all: 'All', model: 'Model', agent: 'Agent', orchestration: 'Workflow', queue: 'Queue' }[filter] || 'All'}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showEntityDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showEntityDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'model', label: 'Model' },
                    { value: 'agent', label: 'Agent' },
                    { value: 'orchestration', label: 'Workflow' },
                    { value: 'queue', label: 'Queue' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleFilterChange(option.value)}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                    >
                      <span className={filter === option.value ? 'font-medium text-primary-600' : 'text-gray-700 dark:text-gray-300'}>{option.label}</span>
                      {filter === option.value && <Check className="w-4 h-4 text-primary-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Time filter - only show if checkpoints exist */}
          {latestCheckpoint && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Time:</span>
              <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowTimeDropdown(false); }}>
                <button
                  type="button"
                  onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white hover:bg-gray-50 dark:hover:bg-gray-700 min-w-[180px]"
                  aria-label="Filter by time"
                >
                  <span>{timeFilter === 'all' ? 'All time' : 'Since last checkpoint'}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTimeDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showTimeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                    {[
                      { value: 'all' as const, label: 'All time' },
                      { value: 'since_checkpoint' as const, label: 'Since last checkpoint' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleTimeFilterChange(option.value)}
                        className="flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                      >
                        <span className={timeFilter === option.value ? 'font-medium text-primary-600' : 'text-gray-700 dark:text-gray-300'}>{option.label}</span>
                        {timeFilter === option.value && <Check className="w-4 h-4 text-primary-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Change list */}
      <ul role="list" className={compact ? "space-y-1" : "space-y-2"}>
        {changes.map((change) => (
          <li
            key={change.change_id}
            data-testid={`change-row-${change.change_id}`}
            className={compact
              ? "p-3 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-sm transition-shadow"
              : "p-4 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-sm transition-shadow"
            }
          >
            {compact ? (
              /* Compact layout - vertical stack for narrow panels */
              <div className="space-y-2">
                {/* Header row: icon, entity name, time */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={`p-1.5 rounded ${getEntityIconClasses(change.entity_type)}`}
                      data-testid={`entity-icon-${change.entity_type}`}
                    >
                      <EntityTypeIcon entityType={change.entity_type} />
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                      {change.entity_name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatRelativeTime(change.created_at)}
                  </span>
                </div>

                {/* Badges row: operation, entity type, new */}
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded font-medium ${getOperationBadgeClasses(change.operation)}`}
                  >
                    {change.operation}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {{ orchestration: 'Workflow', agent: 'Agent', model: 'Model', queue: 'Queue' }[change.entity_type] || change.entity_type}
                  </span>
                  {isNewChange(change, highlightChangesSince) && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-500 text-white"
                      data-testid={`new-badge-${change.change_id}`}
                    >
                      New
                    </span>
                  )}
                </div>

                {/* Changed fields - with step summary for workflows */}
                {change.entity_type === 'orchestration' &&
                 change.changed_fields?.includes('steps') &&
                 stepChangesMap.get(change.change_id) ? (
                  <div className="flex flex-wrap items-center gap-1">
                    <StepChangeBadges stepChanges={stepChangesMap.get(change.change_id)!} />
                    {change.changed_fields.filter(f => f !== 'steps').length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {change.changed_fields.filter(f => f !== 'steps').join(', ')}
                      </span>
                    )}
                  </div>
                ) : change.changed_fields && change.changed_fields.length > 0 ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="text-gray-400">Changed: </span>
                    {change.changed_fields.length <= 3
                      ? change.changed_fields.join(', ')
                      : `${change.changed_fields.slice(0, 3).join(', ')} +${change.changed_fields.length - 3} more`
                    }
                  </div>
                ) : null}

                {/* Actions row */}
                <div className="flex items-center gap-1.5 pt-1 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => handleViewDetails(change)}
                    disabled={loadingDetailId === change.change_id}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                    aria-label="View Details"
                  >
                    {loadingDetailId === change.change_id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                    Details
                  </button>

                  {change.operation !== 'delete' && (
                    <Tooltip content={isLatestChangeForEntity(change, changes) ? 'This is already the current state' : ''}>
                    <button
                      onClick={() => handleReapply(change.change_id)}
                      disabled={reapplyingId === change.change_id || isLatestChangeForEntity(change, changes)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded hover:bg-primary-100 transition-colors disabled:opacity-50"
                      aria-label={isLatestChangeForEntity(change, changes) ? 'Already current' : 'Re-apply'}
                    >
                      <RotateCcw className="w-3 h-3" />
                      Re-apply
                    </button>
                    </Tooltip>
                  )}

                  {(change.operation === 'update' || change.operation === 'undo' || change.operation === 'delete') && showUndoConfirm === null && (
                    <button
                      onClick={() => handleUndoClick(change.change_id)}
                      disabled={undoingId === change.change_id}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
                      aria-label="Undo"
                    >
                      {undoingId === change.change_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Undo2 className="w-3 h-3" />
                      )}
                      Undo
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Standard layout - horizontal with actions on right */
              <div className="flex items-start gap-3">
                {/* Entity type icon */}
                <div
                  className={`p-2 rounded-lg flex-shrink-0 ${getEntityIconClasses(change.entity_type)}`}
                  data-testid={`entity-icon-${change.entity_type}`}
                >
                  <EntityTypeIcon entityType={change.entity_type} />
                </div>

                {/* Change info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                      {change.entity_name}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {formatRelativeTime(change.created_at)}
                    </span>
                  </div>

                  {/* Project badge, new badge, and entity type */}
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-medium ${getOperationBadgeClasses(change.operation)}`}
                    >
                      {change.operation}
                    </span>
                    {isNewChange(change, highlightChangesSince) && (
                      <span
                        className="text-xs px-2 py-0.5 rounded font-medium bg-blue-500 text-white"
                        data-testid={`new-badge-${change.change_id}`}
                      >
                        New
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {{ orchestration: 'Workflow', agent: 'Agent', model: 'Model', queue: 'Queue' }[change.entity_type] || change.entity_type}
                    </span>
                  </div>

                  {/* Changed fields - with step summary for workflows */}
                  {change.entity_type === 'orchestration' &&
                   change.changed_fields?.includes('steps') &&
                   stepChangesMap.get(change.change_id) ? (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <StepChangeBadges stepChanges={stepChangesMap.get(change.change_id)!} />
                      {change.changed_fields.filter(f => f !== 'steps').length > 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {change.changed_fields.filter(f => f !== 'steps').join(', ')}
                        </span>
                      )}
                    </div>
                  ) : change.changed_fields && change.changed_fields.length > 0 ? (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Changed: {change.changed_fields.join(', ')}
                    </div>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleViewDetails(change)}
                    disabled={loadingDetailId === change.change_id}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="View Details"
                  >
                    {loadingDetailId === change.change_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                    {loadingDetailId === change.change_id ? 'Loading...' : 'Details'}
                  </button>

                  {/* Only show Re-apply for non-delete projects, disabled if latest for entity */}
                  {change.operation !== 'delete' && (
                    <Tooltip content={isLatestChangeForEntity(change, changes) ? 'This is already the current state' : ''}>
                    <button
                      onClick={() => handleReapply(change.change_id)}
                      disabled={reapplyingId === change.change_id || isLatestChangeForEntity(change, changes)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={isLatestChangeForEntity(change, changes) ? 'Re-apply disabled (already current state)' : 'Re-apply'}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      {reapplyingId === change.change_id ? 'Applying...' : 'Re-apply'}
                    </button>
                    </Tooltip>
                  )}

                  {/* Show Undo for update, undo, and delete operations - hide row buttons when confirm dialog is open OR when undoing */}
                  {(change.operation === 'update' || change.operation === 'undo' || change.operation === 'delete') && showUndoConfirm === null && (
                    <button
                      onClick={() => handleUndoClick(change.change_id)}
                      disabled={undoingId === change.change_id}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={undoingId === change.change_id ? 'Undoing' : 'Undo'}
                    >
                      {undoingId === change.change_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Undo2 className="w-3.5 h-3.5" />
                      )}
                      {undoingId === change.change_id ? 'Undoing...' : 'Undo'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Load More button */}
      {hasMore && (
        <div className="text-center pt-2">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Load More"
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {/* Change Detail Modal */}
      <ChangeDetailModal
        isOpen={modalOpen}
        change={selectedChange}
        projectId={projectId}
        onClose={handleModalClose}
        onReapplied={handleReapplied}
        onUndone={handleUndone}
        isLatestForEntity={selectedChange ? isLatestChangeForEntity(selectedChange, changes) : false}
      />

      {/* Undo Confirmation Dialog */}
      {showUndoConfirm !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleUndoCancel}
          />

          {/* Dialog */}
          <div className="relative bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
              Confirm Undo Change
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              This will revert the entity to its previous state. A new change
              record will be created to track this undo project. Are you sure?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleUndoCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleUndoConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                aria-label="Confirm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeList;
